import { type FC, type ReactNode, useRef } from 'react'
import classNames from 'classnames'
import pluralize from 'pluralize'
import { type InsightsDashboardData } from '../store/insights'
import { useScrollLock } from '../hooks/useScrollLock'
import { type TimeRangeKey, TIME_RANGE_TO_DAYS } from '../utils/insightsTransforms'
import { fmt } from '../utils/insightsUtils'
import { type InsightsTrackingClient, NoopInsightsTrackingClient } from '../utils/tracking'
import { InsightsSection, EmptyState } from './insightsCommon'
import { HighlightsSection } from './HighlightsSection'
import { DiscussionTopicsSection } from './DiscussionTopicsSection'
import { ClientOutlookSection } from './ClientOutlookSection'
import { AssetOpportunitiesSection } from './AssetOpportunitiesSection'
import { ReferralsSection } from './ReferralsSection'
import { InsightsEmptyState } from './InsightsEmptyState/InsightsEmptyState'
import './InsightsDashboardLayout.scss'

const TIME_RANGES = Object.keys(TIME_RANGE_TO_DAYS) as TimeRangeKey[]

const formatDateRange = (timeRange: TimeRangeKey): string => {
  const now = new Date()
  const start = new Date(now)
  start.setDate(now.getDate() - TIME_RANGE_TO_DAYS[timeRange])
  const dateFormatter = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short' })
  return `${dateFormatter.format(start)} – ${dateFormatter.format(now)}`
}

interface InsightsDashboardLayoutProps {
  data: InsightsDashboardData | null
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  timeRange: TimeRangeKey
  onTimeRangeChange: (r: TimeRangeKey) => void
  onExportCsv?: () => void | Promise<unknown>
  trackingClient?: InsightsTrackingClient
  controlsLeft?: ReactNode
  feedbackCTA?: ReactNode
  subtitleUserCount?: number
  title?: string
  controlsLayout?: 'inline' | 'below'
}

export const InsightsDashboardLayout: FC<InsightsDashboardLayoutProps> = ({
  data,
  isLoading,
  isFetching,
  isError,
  timeRange,
  onTimeRangeChange,
  onExportCsv,
  trackingClient = NoopInsightsTrackingClient,
  controlsLeft,
  feedbackCTA,
  subtitleUserCount = 0,
  title = 'Insights',
  controlsLayout = 'inline',
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const isInitialLoading = isLoading && !data
  const isRefetching = isFetching && !isLoading && !!data
  const showEmptyState = data?.highlights.meetingCount === 0
  useScrollLock(containerRef, isRefetching)

  const subtitle = data
    ? `Based on ${fmt(data.highlights.meetingCount)} ${pluralize('meeting', data.highlights.meetingCount)}${
        subtitleUserCount > 0 ? ` across ${subtitleUserCount} ${pluralize('user', subtitleUserCount)}` : ''
      } captured ${formatDateRange(timeRange)}`
    : ''

  const controls = (
    <>
      {controlsLeft}
      <div className='insights-time-range-selector' role='group'>
        {TIME_RANGES.map((range) => (
          <button
            key={range}
            type='button'
            className={classNames('insights-time-range-btn', { 'insights-time-range-btn--active': timeRange === range })}
            onClick={() => {
              onTimeRangeChange(range)
              trackingClient.track('time_range_changed', { timeRange: range })
            }}
          >
            {range}
          </button>
        ))}
      </div>
      {onExportCsv && (
        <>
          <div className='vr' />
          <button
            type='button'
            className='insights-export-button'
            onClick={() => onExportCsv()}
            disabled={isInitialLoading || isRefetching || !data || showEmptyState}
          >
            <i className='bi bi-download' />
            <span>Export as CSV</span>
          </button>
        </>
      )}
    </>
  )

  return (
    <div ref={containerRef} className='insights-dashboard'>
      {controlsLayout === 'below' ? (
        <>
          <div>
            <h1 className='h1 fw-bolder'>{title}</h1>
            {data && <p className='text-muted mb-0 small'>{subtitle}</p>}
          </div>
          <div className='d-flex justify-content-between align-items-center insights-controls-row'>
            <div className='d-flex align-items-center gap-3'>
              {controlsLeft}
              <div className='insights-time-range-selector' role='group'>
                {TIME_RANGES.map((range) => (
                  <button
                    key={range}
                    type='button'
                    className={classNames('insights-time-range-btn', { 'insights-time-range-btn--active': timeRange === range })}
                    onClick={() => {
                      onTimeRangeChange(range)
                      trackingClient.track('time_range_changed', { timeRange: range })
                    }}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
            {onExportCsv && (
              <button
                type='button'
                className='insights-export-button'
                onClick={() => onExportCsv()}
                disabled={isInitialLoading || isRefetching || !data || showEmptyState}
              >
                <i className='bi bi-download' />
                <span>Export as CSV</span>
              </button>
            )}
          </div>
        </>
      ) : (
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <h1 className='h1 fw-bolder'>{title}</h1>
            {data && <p className='text-muted mb-0 small'>{subtitle}</p>}
          </div>
          <div className='d-flex align-items-center gap-3'>{controls}</div>
        </div>
      )}

      {isInitialLoading && (
        <div className='insights-initial-loading'>
          <div className='insights-loading-spinner' />
          <div className='insights-loading-text'>Pulling up insights...</div>
        </div>
      )}

      {isError && !data && (
        <div className='insights-initial-loading'>
          <EmptyState
            icon={<i className='bi bi-exclamation-triangle' />}
            title='Something went wrong'
            subtitle="We couldn't load your insights. Please try again later."
          />
        </div>
      )}

      {isRefetching && (
        <div className='insights-filter-loading-overlay'>
          <div className='insights-filter-loading-overlay__content'>
            <div className='insights-loading-spinner' />
            <div className='insights-loading-text insights-loading-text--light'>Pulling up insights...</div>
          </div>
        </div>
      )}

      {data && !showEmptyState && (
        <div className='position-relative sections-container'>
          <HighlightsSection highlights={data.highlights} timeRange={timeRange} />

          <h2 className='insights-explore-title mt-3 mb-3'>Explore the data behind the highlights</h2>
          <DiscussionTopicsSection topics={data.discussionTopics} products={data.investmentProducts} trackingClient={trackingClient} />

          {feedbackCTA}

          {data.clientOutlook ? (
            <ClientOutlookSection data={data.clientOutlook} />
          ) : (
            <InsightsSection title='Client outlook' subtitle='Clients expressed sentiment about specific asset classes'>
              <EmptyState icon={<i className='bi bi-binoculars' />} title='No sentiment detected' subtitle='Try selecting a wider time range' />
            </InsightsSection>
          )}

          {data.assetOpportunities ? (
            <AssetOpportunitiesSection data={data.assetOpportunities} trackingClient={trackingClient} />
          ) : (
            <InsightsSection title='Asset opportunities' subtitle='Track how held-away assets move from mention to client agreement'>
              <EmptyState icon={<i className='bi bi-bullseye' />} title='No asset mentions' subtitle='Try selecting a wider time range' />
            </InsightsSection>
          )}

          {data.referrals ? (
            <ReferralsSection data={data.referrals} trackingClient={trackingClient} />
          ) : (
            <InsightsSection title='Referrals' subtitle='See where referrals are coming from and where to ask more'>
              <EmptyState icon={<i className='bi bi-people' />} title='No referral activity' subtitle='Try selecting a wider time range' />
            </InsightsSection>
          )}
        </div>
      )}
      {showEmptyState && <InsightsEmptyState />}
    </div>
  )
}
